import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { SidebarNode } from './panel/useDynamicMenu';

/**
 * Sidebar menu tree cached in Redux.
 *
 * Why a dedicated slice
 * ---------------------
 * `useDynamicMenu` used to keep its own module-level cache — that works
 * for a single consumer (PanelLayout) but breaks the moment a second
 * component needs the same tree (e.g. a topbar quick-nav, a command
 * palette, a mobile drawer): each mount re-runs the effect, duplicates
 * the fetch, and can render before the shared cache warms up.
 *
 * The slice fixes that by making the tree a normal Redux value —
 * multiple `useMenuTree()` callers share the same rows, the first
 * fetch populates it, subsequent mounts read straight from the store.
 *
 * Kept in memory only (NOT persisted) — the menu list can change
 * server-side any time the tenant admin edits RoleMenu, so a cold-
 * store fetch on each app boot is deliberate.
 */
export interface MenuSliceState {
  /** Filtered tree already trimmed to the current user's role. */
  nodes: SidebarNode[];
  /** True when the user's role is on the admin allowlist (bypass). */
  isAdmin: boolean;
  /**
   * True when the current non-admin user has at least one RoleMenu
   * grant. False → sidebar should show the "no menus assigned" state
   * instead of falling back to the static tree.
   */
  hasMenusAssigned: boolean;
  /** epoch ms of the last successful load — drives TTL invalidation. */
  loadedAt: number | null;
  /** Cache key: the role name the tree was fetched for. */
  cachedForRole: string | null;
  /** Non-null while an error message from the last fetch attempt applies. */
  error: string | null;
}

const initialState: MenuSliceState = {
  nodes: [],
  isAdmin: false,
  hasMenusAssigned: false,
  loadedAt: null,
  cachedForRole: null,
  error: null,
};

export interface MenuTreePayload {
  nodes: SidebarNode[];
  isAdmin: boolean;
  hasMenusAssigned: boolean;
  role: string;
}

const menuSlice = createSlice({
  name: 'menu',
  initialState,
  reducers: {
    setMenuTree(state, action: PayloadAction<MenuTreePayload>) {
      state.nodes = action.payload.nodes;
      state.isAdmin = action.payload.isAdmin;
      state.hasMenusAssigned = action.payload.hasMenusAssigned;
      state.cachedForRole = action.payload.role;
      state.loadedAt = Date.now();
      state.error = null;
    },
    setMenuError(state, action: PayloadAction<string>) {
      state.error = action.payload;
    },
    /**
     * Clear the cache so the next `useDynamicMenu` mount refetches.
     * Called after a RoleMenu admin edit and after logout.
     */
    resetMenuTree(state) {
      state.nodes = [];
      state.isAdmin = false;
      state.hasMenusAssigned = false;
      state.loadedAt = null;
      state.cachedForRole = null;
      state.error = null;
    },
  },
});

export const { setMenuTree, setMenuError, resetMenuTree } = menuSlice.actions;
export default menuSlice.reducer;
